// Admin dark mode. Each browser stores a preference — "light", "dark" or
// "system" (the default) — and "system" follows prefers-color-scheme live.
//
// The inline script in the admin layout <head> applies the preference before
// first paint; keep its storage key and resolution in sync with this file.
// This module keeps the theme right after that: across Turbo visits (which
// keep the <html> element, so leaving the admin must restore ford-light),
// OS theme changes, other tabs changing the preference, and Chartkick charts.

import Chart from "chart.js/auto"

export const STORAGE_KEY = "ford-admin-theme"
export const PREFERENCES = ["light", "dark", "system"]
export const CHANGE_EVENT = "admin-theme:change"

const DARK_QUERY = window.matchMedia("(prefers-color-scheme: dark)")

// Chart.js paints on a canvas, so CSS theme tokens can't reach it. Light keeps
// Chart.js's own defaults; dark uses hex equivalents of ford-dark's tokens.
const CHART_LIGHT = {
  color: Chart.defaults.color,
  borderColor: Chart.defaults.borderColor,
  arcBorderColor: Chart.defaults.elements.arc.borderColor
}
const CHART_DARK = {
  color: "#aab1bf",
  borderColor: "rgba(255, 255, 255, 0.1)",
  arcBorderColor: "#1a2029"
}

// Set only when localStorage refuses a write (private mode, policy), so the
// choice still holds for this tab's Turbo visits and OS theme changes.
let unsavedPreference = null

export function storedPreference() {
  if (unsavedPreference) return unsavedPreference
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return PREFERENCES.includes(value) ? value : "system"
  } catch {
    return "system"
  }
}

export function setPreference(preference) {
  if (!PREFERENCES.includes(preference)) return
  try {
    localStorage.setItem(STORAGE_KEY, preference)
    unsavedPreference = null
  } catch {
    unsavedPreference = preference
  }
  applyTheme(document.body, preference)
}

function isAdmin(body) {
  return !!body && body.hasAttribute("data-admin")
}

function resolve(preference) {
  if (preference === "system") return DARK_QUERY.matches ? "ford-dark" : "ford-light"
  return preference === "dark" ? "ford-dark" : "ford-light"
}

// `body` is the body about to be shown — on turbo:before-render that's the
// incoming one, which decides whether we're still inside the admin.
export function applyTheme(body = document.body, preference = storedPreference()) {
  const root = document.documentElement
  const admin = isAdmin(body)
  const theme = admin ? resolve(preference) : "ford-light"

  if (admin) root.dataset.themePref = preference
  else delete root.dataset.themePref

  root.dataset.theme = theme
  // Compare with what the charts were last styled for, not the attribute:
  // the inline <head> script may already have set it (e.g. Turbo merging
  // the admin <head> after sign-in) without Chart.js hearing about it.
  if (chartTheme !== theme) themeCharts()
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { preference, theme } }))
}

// Charts read Chart.defaults when created, so set them before Chartkick draws
// (application.js imports this module first) and redraw on every switch.
let chartTheme = null

function themeCharts() {
  chartTheme = document.documentElement.dataset.theme
  const palette = chartTheme === "ford-dark" ? CHART_DARK : CHART_LIGHT
  Chart.defaults.color = palette.color
  Chart.defaults.borderColor = palette.borderColor
  Chart.defaults.elements.arc.borderColor = palette.arcBorderColor

  window.Chartkick?.eachChart((chart) => chart.redraw())
}

// Series colors that vanish on a dark card get a dark-mode replacement via
// `library: { plugins: { adminTheme: { darkColors: [...] } } }` on the chart.
// Redraws rebuild datasets from the light `colors:`, so light needs no undo.
Chart.register({
  id: "adminTheme",
  beforeUpdate(chart, _args, options) {
    const colors = options.darkColors && Array.from(options.darkColors)
    if (!colors || document.documentElement.dataset.theme !== "ford-dark") return

    chart.data.datasets.forEach((dataset, index) => {
      // Pie/doughnut: one dataset, one color per slice.
      if (Array.isArray(dataset.backgroundColor)) {
        dataset.backgroundColor = colors
        return
      }
      const color = colors[index % colors.length]
      Object.assign(dataset, {
        borderColor: color,
        backgroundColor: color,
        pointBackgroundColor: color,
        pointHoverBackgroundColor: color
      })
    })
  }
})

themeCharts()

document.addEventListener("turbo:before-render", (event) => applyTheme(event.detail.newBody))

DARK_QUERY.addEventListener("change", () => {
  if (storedPreference() === "system") applyTheme()
})

window.addEventListener("storage", (event) => {
  if (event.key === STORAGE_KEY) applyTheme()
})
